import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  LayoutDashboard, Users, MapPin, Layers, FileText, BarChart3, Shield, LogOut,
  Menu, X, Upload, ClipboardList, ChevronLeft, ChevronRight,
  Image
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { BACKEND_URL } from '../lib/config';

const adminNavItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/coaches', label: 'Coaches', icon: Users },
  { path: '/admin/locations', label: 'Locations & Batches', icon: MapPin },
  { path: '/admin/cycles', label: 'Evaluation Cycles', icon: Layers },
  { path: '/admin/templates', label: 'Templates', icon: FileText },
  { path: '/admin/branding', label: 'Branding', icon: Image },

  { path: '/admin/evaluations', label: 'All Evaluations', icon: ClipboardList },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: Shield },
];

const coachNavItems = [
  { path: '/coach/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/coach/new-evaluation', label: 'New Evaluation', icon: Upload },
  { path: '/coach/evaluations', label: 'My Evaluations', icon: ClipboardList },
  { path: '/coach/analytics', label: 'Analytics', icon: BarChart3 },
];

const NavItem = ({ item, collapsed, mobile, onClose }) => {
  const location = useLocation();
  const isActive = location.pathname === item.path ||
    (item.path !== '/admin/dashboard' && item.path !== '/coach/dashboard' &&
      location.pathname.startsWith(item.path));
  const Icon = item.icon;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.path}
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive
                ? 'nav-active'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            } ${collapsed && !mobile ? 'justify-center px-2' : ''}`}
          >
            <Icon size={18} className="flex-shrink-0" />
            {(!collapsed || mobile) && <span>{item.label}</span>}
          </Link>
        </TooltipTrigger>
        {collapsed && !mobile && (
          <TooltipContent side="right">
            <p>{item.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.getPublicLogo()
      .then((res) => {
        const value = res?.data?.logo;
        if (mounted && typeof value === 'string' && value.startsWith('data:image/')) {
          setLogo(value);
        }
      })
      .catch(() => {
        // Keep the text fallback when the public branding endpoint is unavailable.
      });
    return () => { mounted = false; };
  }, []);

  const navItems = isAdmin ? adminNavItems : coachNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-[#0D1117] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`hidden md:flex flex-col bg-[#111827] border-r border-border/60 transition-all duration-200 ${
          collapsed ? 'w-[60px]' : 'w-[240px]'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 p-4 border-b border-border/60 ${
          collapsed ? 'justify-center' : ''
        }`}>
          <div className="w-8 h-8 bg-[#2BB5D8] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {logo ? (
              <img
                src={logo}
                alt="Pitch Basketball Academy logo"
                onError={() => setLogo(null)}
                className="max-h-full max-w-full object-contain p-0.5"
              />
            ) : (
              <span className="text-[#0D1117] font-bold text-xs">P</span>
            )}
          </div>
          {!collapsed && (
            <div>
              <p className="font-bold text-sm text-foreground leading-none">PITCH</p>
              <p className="text-[10px] text-muted-foreground">Basketball Academy</p>
            </div>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {!collapsed && (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2">
              {isAdmin ? 'Administration' : 'Coach'}
            </p>
          )}
          {navItems.map(item => <NavItem key={item.path} item={item} collapsed={collapsed} onClose={() => setMobileOpen(false)} />)}
        </nav>

        {/* Bottom */}
        <div className="p-2 border-t border-border/60 space-y-1">
          {!collapsed && (
            <div className="px-3 py-2 rounded-xl bg-secondary/50">
              <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
              <Badge variant="outline" className="text-[10px] mt-1 border-primary/30 text-primary">
                {user?.role === 'admin' ? 'Super Admin' : 'Coach'}
              </Badge>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={`w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 ${
              collapsed ? 'px-2 justify-center' : 'justify-start gap-2'
            }`}
          >
            <LogOut size={16} />
            {!collapsed && 'Logout'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className={`w-full text-muted-foreground hover:text-foreground ${
              collapsed ? 'px-2 justify-center' : 'justify-end gap-2'
            }`}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <div
        className={`fixed left-0 top-0 h-full w-[260px] bg-[#111827] border-r border-border/60 z-50 transform transition-transform md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2BB5D8] rounded-lg flex items-center justify-center overflow-hidden">
              {logo ? (
                <img
                  src={logo}
                  alt="Pitch Basketball Academy logo"
                  onError={() => setLogo(null)}
                  className="max-h-full max-w-full object-contain p-0.5"
                />
              ) : (
                <span className="text-[#0D1117] font-bold text-xs">P</span>
              )}
            </div>
            <div>
              <p className="font-bold text-sm">PITCH</p>
              <p className="text-[10px] text-muted-foreground">Basketball Academy</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
            <X size={16} />
          </Button>
        </div>
        <nav className="p-3 space-y-0.5">
          {navItems.map(item => <NavItem key={item.path} item={item} collapsed={false} mobile onClose={() => setMobileOpen(false)} />)}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-border/60">
          <div className="px-3 py-2 rounded-xl bg-secondary/50 mb-2">
            <p className="text-xs font-medium">{user?.name}</p>
            <p className="text-[10px] text-muted-foreground">{user?.email}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive">
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-[#111827]/80 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <h1 className="text-sm font-semibold text-foreground">
                Pitch Evaluation System
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] border-primary/30 text-primary hidden sm:flex">
              {user?.role === 'admin' ? 'Super Admin' : 'Coach'}
            </Badge>
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto main-content">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-30 bg-[#111827]/95 backdrop-blur border-t border-border/60 md:hidden"
      >
        <div className="flex items-center justify-around">
          {navItems.slice(0, 4).map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 py-2 px-3 min-w-[60px] ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {isActive && (
                  <div className="absolute top-0 h-0.5 w-8 bg-primary rounded-full" />
                )}
                <Icon size={20} />
                <span className="text-[10px] font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
